/* eslint-disable @typescript-eslint/indent */
import { getBFFApiClient } from '@acp/feature/auth';
import {
    PropertyAccessIssuesData, AccessIssueProblemCase,
    ActiveProblems, APIResponse, TenancyDetail,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';
import { AccessIssueFilters } from './types/filters';

async function getActiveAccessIssuesForProperty(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<ActiveProblems>>(`/v1/property/${propertyId}/access-issues/active`);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get active access issues.');
    }

    return response.data.data;
}

async function getAccessIssues(
    params: AccessIssueFilters,
    pageNumber: number,
    pageSize: number
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryParams: any = {
        query: params.query ?? '',
        facet: params.facets,
        page: pageNumber,
        propertyId: params.propertyId,
        'property-manager': [params.propertyManager],
        'page-size': pageSize,
    };

    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: PropertyAccessIssuesData[];
        unfilteredTotal: number;
    }>>('/v1/agency/access-issues', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: queryParams,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get properties: ${response.data.error}`);
    }

    return response.data.data;
}

// TODO: Get rid of this.
async function getPropertyTenancy(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<TenancyDetail>>(`/v1/property/${propertyId}/tenancy`);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property tenancy');
    }

    return response.data.data;
}

async function getAccessIssuesByPropertyV2(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<AccessIssueProblemCase>>(`/v2/property/${propertyId}/access-issues`);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get access issues.');
    }

    return response.data.data;
}

async function resolveAccessIssueByProblemResolved(
    propertyId: number,
    problemLabel: string,
    notes?: string
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${propertyId}/access-issues/resolve`, {
        problemLabel,
        notes,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to resolve access issue.');
    }
}

async function resolveAccessIssueByAgencyAction(
    propertyId: number,
    solutionLabel: string,
    notes?: string,
    date?: Date
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${propertyId}/access-issues/action`, {
        solutionLabel,
        notes,
        date,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to resolve access issue.');
    }
}

async function getUrgentAccessIssues(propertyManagerId?: string) {
    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: PropertyAccessIssuesData[];
    }>>(
        '/v1/agency/access-issues/analytics/urgent',
        {
            params: {
                propertyManagerId,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get urgent access issues: ${response.data.error}`);
    }

    return response.data.data;
}

export {
    getActiveAccessIssuesForProperty,
    getAccessIssues,
    getPropertyTenancy,
    getAccessIssuesByPropertyV2,
    resolveAccessIssueByAgencyAction,
    resolveAccessIssueByProblemResolved,
    getUrgentAccessIssues,
};
